/**
 * Simplified Health Data Service
 * Uses the existing backend /api/health endpoints
 */

import { HealthDataForm } from "../types/fhir-health";
import { api } from "./api";
import { log } from "@/lib/log";

const LEGACY_ALLERGY_TRANSLATIONS: Record<string, string> = {
  Nüsse: "Nuts",
  Eier: "Eggs",
  Kuhimilch: "Cow's milk",
  Meeresfrüchte: "Seafood",
  Schokolade: "Chocolate",
  Erdnüsse: "Peanuts",
  Bienenstiche: "Bee stings",
  Wespenstiche: "Wasp stings",
  Anästhetika: "Anesthetics",
};

const normalizeAllergyLabel = (value: string) =>
  LEGACY_ALLERGY_TRANSLATIONS[value] || value;

class HealthDataService {
  /**
   * Get patient's health records
   */
  async getHealthData(
    _patientId: string,
    _token: string,
  ): Promise<HealthDataForm | null> {
    try {
      log.debug("🔍 Fetching health data from user profile");

      // Get user profile data first
      const profileResponse = await api.get("/users/profile");
      log.debug("✅ Profile data response:", profileResponse.data);
      log.debug("✅ Profile data.data:", profileResponse.data.data);
      log.debug(
        "✅ User object from profile:",
        profileResponse.data.data?.user,
      );
      log.debug(
        "✅ User dateOfBirth:",
        profileResponse.data.data?.user?.dateOfBirth,
      );
      log.debug(
        "✅ User avatarUrl:",
        profileResponse.data.data?.user?.avatarUrl,
      );

      // Check if user data exists - FIXED: data is nested in data.data.user
      if (!profileResponse.data?.data?.user) {
        log.debug("📝 No profile found, returning default structure");
        return {
          gender: "",
          bloodType: "",
          weight: { value: 0, unit: "kg" },
          height: { value: 0, unit: "cm" },
          bloodPressure: {
            systolic: 120,
            diastolic: 80,
            unit: "mmHg",
            recordedAt: new Date().toISOString(),
          },
          restingHeartRate: {
            value: 70,
            unit: "bpm",
            recordedAt: new Date().toISOString(),
          },
          physicalWellbeing: 3,
          emotionalWellbeing: 3,
          wellbeingRecorded: new Date().toISOString(),
          wellbeingScore: 3,
          currentComplaints: "",
          medicalIncidents: [],
          foodIntolerances: [],
          medicationAllergies: [],
          coagulationDisorder: false,
          heartAttack: { hasHad: false },
          stroke: { hasHad: false },
          // Phase 1: Critical Travel Health Data
          immunizations: [],
          labResults: [],
          extendedAllergies: [],
        };
      }

      // User data is in response.data.data.user (Backend wraps it, then Axios wraps it again)
      const user = profileResponse.data.data.user;

      // Load current vital signs data – start with nulls so we can detect "no data"
      const vitalSignsData: {
        systolic: number | null;
        diastolic: number | null;
        restingHeartRate: number | null;
        recordedAt: string;
      } = {
        systolic: null,
        diastolic: null,
        restingHeartRate: null,
        recordedAt: new Date().toISOString(),
      };

      try {
        log.debug("🔍 Fetching latest vital signs...");
        const vitalSignsResponse = await api.get("/vital-signs/latest");
        if (
          vitalSignsResponse.data?.success &&
          vitalSignsResponse.data.data?.latestVitalSigns
        ) {
          const vitals = vitalSignsResponse.data.data.latestVitalSigns;
          // Only override defaults if real values exist (no || fallback to avoid masking "no data")
          if (vitals.systolicBloodPressure != null)
            vitalSignsData.systolic = vitals.systolicBloodPressure;
          if (vitals.diastolicBloodPressure != null)
            vitalSignsData.diastolic = vitals.diastolicBloodPressure;
          if (vitals.restingHeartRate != null)
            vitalSignsData.restingHeartRate = vitals.restingHeartRate;
          if (vitals.recordedAt || vitalSignsResponse.data.data.recordedAt)
            vitalSignsData.recordedAt =
              vitals.recordedAt ||
              vitalSignsResponse.data.data.recordedAt ||
              new Date().toISOString();
          log.debug("✅ Loaded vital signs data:", vitalSignsData);
        }
      } catch (vitalError) {
        log.debug("📝 No vital signs found, using defaults");
      }

      const rawAllergies = Array.isArray(user.allergies)
        ? user.allergies.map((allergy: string) =>
            normalizeAllergyLabel(allergy),
          )
        : [];
      const medicationKeywords = [
        "Penicillin",
        "Aspirin",
        "Ibuprofen",
        "Amoxicillin",
        "Paracetamol",
      ];
      const extendedAllergies = rawAllergies.map(
        (allergy: string, index: number) => ({
          id: `${index}-${allergy}`,
          code: "",
          display: allergy,
          category: medicationKeywords.includes(allergy)
            ? "medication"
            : "food",
          criticality: "low" as const,
          clinicalStatus: "active" as const,
          onsetDateTime: "",
          reactions: [],
        }),
      );

      // Transform user data to health data format
      const healthData: HealthDataForm = {
        gender: user.gender || "",
        bloodType: user.bloodType || "",
        weight: { value: user.weight || 0, unit: "kg" },
        height: { value: user.height || 0, unit: "cm" },
        // Only set vital signs if real FHIR data exists (null = no data → dashboard hides the card)
        bloodPressure:
          vitalSignsData.systolic != null && vitalSignsData.diastolic != null
            ? {
                systolic: vitalSignsData.systolic,
                diastolic: vitalSignsData.diastolic,
                unit: "mmHg",
                recordedAt: vitalSignsData.recordedAt,
              }
            : undefined,
        restingHeartRate:
          vitalSignsData.restingHeartRate != null
            ? {
                value: vitalSignsData.restingHeartRate,
                unit: "bpm",
                recordedAt: vitalSignsData.recordedAt,
              }
            : undefined,
        physicalWellbeing: 3,
        emotionalWellbeing: 3,
        wellbeingRecorded: new Date().toISOString(),
        wellbeingScore: 3, // Legacy field
        currentComplaints: "",
        medicalIncidents: [],
        foodIntolerances: rawAllergies.filter(
          (allergy: string) => !medicationKeywords.includes(allergy),
        ),
        medicationAllergies: rawAllergies.filter((allergy: string) =>
          medicationKeywords.includes(allergy),
        ),
        coagulationDisorder:
          user.chronicConditions?.includes("Gerinnungsstörung") || false,
        heartAttack: {
          hasHad: user.chronicConditions?.includes("Herzinfarkt") || false,
        },
        stroke: {
          hasHad: user.chronicConditions?.includes("Schlaganfall") || false,
        },
        // Phase 1: Critical Travel Health Data
        immunizations: [],
        labResults: [],
        extendedAllergies,
      };

      // Calculate age if birthdate available
      if (user.dateOfBirth) {
        const birthDate = new Date(user.dateOfBirth);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (
          monthDiff < 0 ||
          (monthDiff === 0 && today.getDate() < birthDate.getDate())
        ) {
          age--;
        }
        healthData.age = age;
      }

      // Calculate BMI if both height and weight are available
      if (healthData.height.value && healthData.weight.value) {
        const heightM = healthData.height.value / 100;
        const bmi =
          Math.round((healthData.weight.value / (heightM * heightM)) * 10) / 10;
        healthData.bmi = bmi;
      }

      log.debug("✅ Transformed health data:", healthData);
      return healthData;
    } catch (error: any) {
      console.error("❌ Error fetching health data:", error);

      // Return default structure instead of null for better UX
      return {
        gender: "",
        bloodType: "",
        weight: { value: 0, unit: "kg" },
        height: { value: 0, unit: "cm" },
        bloodPressure: {
          systolic: 120,
          diastolic: 80,
          unit: "mmHg",
          recordedAt: new Date().toISOString(),
        },
        restingHeartRate: {
          value: 70,
          unit: "bpm",
          recordedAt: new Date().toISOString(),
        },
        physicalWellbeing: 3,
        emotionalWellbeing: 3,
        wellbeingRecorded: new Date().toISOString(),
        wellbeingScore: 3,
        currentComplaints: "",
        medicalIncidents: [],
        foodIntolerances: [],
        medicationAllergies: [],
        coagulationDisorder: false,
        heartAttack: { hasHad: false },
        stroke: { hasHad: false },
        // Phase 1: Critical Travel Health Data
        immunizations: [],
        labResults: [],
        extendedAllergies: [],
      };
    }
  }

  /**
   * Save patient's health data
   */
  async saveHealthData(
    _patientId: string,
    healthData: HealthDataForm,
    _token: string,
  ): Promise<void> {
    try {
      log.debug("💾 Saving health data:", healthData);

      const extendedAllergyNames = (healthData.extendedAllergies || [])
        .map((allergy) => allergy.display?.trim())
        .filter(Boolean)
        .map((allergy) => normalizeAllergyLabel(allergy));

      const mergedAllergies = Array.from(
        new Set([
          ...(healthData.foodIntolerances || []),
          ...(healthData.medicationAllergies || []),
          ...extendedAllergyNames,
        ]),
      );

      // Prepare profile data for /api/users/profile endpoint
      const profileData = {
        // Basic profile data
        gender: healthData.gender,
        bloodType: healthData.bloodType,
        height: healthData.height?.value,
        weight: healthData.weight?.value,

        // Convert arrays to proper format
        allergies: mergedAllergies,
        chronicConditions: [
          healthData.heartAttack?.hasHad ? "Herzinfarkt" : "",
          healthData.stroke?.hasHad ? "Schlaganfall" : "",
          healthData.coagulationDisorder ? "Gerinnungsstörung" : "",
        ].filter(Boolean),
      };

      log.debug("📝 Saving profile data:", profileData);

      // Save to user profile endpoint
      const response = await api.put("/users/profile", profileData);
      log.debug("✅ Profile data saved successfully:", response.data);
    } catch (error: any) {
      console.error("❌ Error saving health data:", error);
      throw new Error("Failed to save health data");
    }
  }

  /**
   * Save vital signs data
   */
  async saveVitalSigns(vitalSignsData: {
    systolicBloodPressure?: number;
    diastolicBloodPressure?: number;
    restingHeartRate?: number;
    recordedAt?: string;
  }): Promise<void> {
    try {
      log.debug("💾 Saving vital signs:", vitalSignsData);

      const payload = {
        systolicBloodPressure: vitalSignsData.systolicBloodPressure,
        diastolicBloodPressure: vitalSignsData.diastolicBloodPressure,
        restingHeartRate: vitalSignsData.restingHeartRate,
        recordedAt: vitalSignsData.recordedAt || new Date().toISOString(),
      };

      const response = await api.post("/vital-signs", payload);
      log.debug("✅ Vital signs saved successfully:", response.data);
    } catch (error: any) {
      console.error("❌ Error saving vital signs:", error);
      throw new Error("Failed to save vital signs");
    }
  }

  /**
   * Get vital signs history for chart
   */
  async getVitalSignsHistory(): Promise<unknown[]> {
    try {
      log.debug("🔍 Fetching vital signs history...");

      const response = await api.get("/vital-signs/history");
      if (response.data?.success && response.data.data?.history) {
        log.debug("✅ Loaded vital signs history:", response.data.data.history);
        return response.data.data.history;
      }

      return [];
    } catch (error: any) {
      console.error("❌ Error fetching vital signs history:", error);
      return [];
    }
  }

  /**
   * Check if health data needs updating
   */
  async checkDataExpiration(
    _patientId: string,
    _token: string,
  ): Promise<unknown> {
    try {
      // For now, return a simple check - could be enhanced later
      return {
        needsUpdate: false,
        expiringItems: [],
      };
    } catch (error: any) {
      console.error("Error checking data expiration:", error);
      return { needsUpdate: false, expiringItems: [] };
    }
  }

  /**
   * Transform frontend health data to backend records format
   */
}

export const healthDataService = new HealthDataService();
